import { useEffect } from 'react';

/**
 * Injects a raw HTML string (potentially containing <script> tags) into a
 * target document location, with scripts actually executing.
 *
 * React's normal innerHTML / dangerouslySetInnerHTML does NOT execute injected
 * <script> elements (W3C spec). To get them to execute we either rewrite the
 * script tag manually OR use Range.createContextualFragment which parses the
 * HTML and DOES run scripts. We use the latter.
 *
 * Props:
 *   html: string  — raw HTML to inject
 *   location: 'head' | 'body-end'  — where to insert
 *
 * Idempotent: each injected fragment is tracked and removed when html/location
 * changes or the component unmounts, so live edits in the admin reload cleanly.
 */
export default function CustomHTMLInjector({ html, location = 'head' }) {
  useEffect(() => {
    if (!html || typeof window === 'undefined') return undefined;

    const target = location === 'body-end' ? document.body : document.head;
    if (!target) return undefined;

    const range = document.createRange();
    range.selectNode(target);
    let fragment;
    try {
      fragment = range.createContextualFragment(html);
    } catch (e) {
      // Malformed HTML — skip injection silently rather than break the page.
      return undefined;
    }
    const injectedNodes = Array.from(fragment.childNodes);
    target.appendChild(fragment);

    return () => {
      injectedNodes.forEach((node) => {
        if (node.parentNode === target) target.removeChild(node);
      });
    };
  }, [html, location]);

  return null;
}
